import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { GoogleMap } from '@capacitor/google-maps';
import { Browser } from '@capacitor/browser';
import { ExternalLink, MapPin as MapPinIcon } from 'lucide-react';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { EmptyState } from '@shared/ui/feedback/EmptyState';
import { HeroHeader } from '@shared/ui/headers/HeroHeader';
import { Section } from '@shared/ui/containers/Section';
import { ListGroup } from '@shared/ui/lists/ListGroup';
import { ListItem } from '@shared/ui/lists/ListItem';
import { pageCopy } from '@shared/lib/pageCopy';
import { platform } from '@shared/lib/platform';
import { fetchMapData, type MapData, type MapPin } from './api';
import './map.css';

function locationMarkerIcon(): L.DivIcon {
  return L.divIcon({
    className: 'map-location-marker',
    html: '<div class="map-location-pin"></div>',
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    popupAnchor: [0, -26],
  });
}

function openInMapsUrl(lat: number, lng: number): string {
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
}

/**
 * Community map — native Capacitor Google Maps on device; Leaflet + OSM on web
 * (avoids Google embed tile 404s and does not require a Maps API key in dev).
 */
export function MapView(): JSX.Element {
  const copy = pageCopy().map;
  const mapQuery = useQuery({ queryKey: ['map'], queryFn: fetchMapData });

  if (mapQuery.isLoading) {
    return (
      <div className="min-h-full bg-paper px-5 pt-5 pb-safe-or-6">
        <Skeleton className="h-10 w-48 mb-4" />
        <Skeleton className="h-[320px] rounded-lg" />
      </div>
    );
  }

  if (mapQuery.isError) {
    return (
      <div className="p-6">
        <ErrorState
          title="Couldn't load the map."
          description="Please try again."
          onRetry={() => {
            void mapQuery.refetch();
          }}
        />
      </div>
    );
  }

  const data = mapQuery.data;
  if (!data || data.pins.length === 0) {
    return (
      <div className="min-h-full bg-paper">
        <HeroHeader
          eyebrow={copy.eyebrow}
          title={copy.title}
          subtitle={copy.subtitle}
        />
        <div className="px-5 pt-4">
          <EmptyState
            icon={<MapPinIcon className="h-7 w-7" strokeWidth={1.5} />}
            title="No map locations yet"
            description="Your estate has not published any locations with map coordinates. Contact the office if you expected to see points here."
          />
        </div>
      </div>
    );
  }

  return platform.isNative() ? <NativeMap data={data} /> : <WebMap data={data} />;
}

function MapChrome({
  data,
  copy,
  children,
}: {
  data: MapData;
  copy: ReturnType<typeof pageCopy>['map'];
  children: ReactNode;
}): JSX.Element {
  const sortedPins = useMemo(
    () => [...data.pins].sort((a, b) => a.label.localeCompare(b.label)),
    [data.pins],
  );

  return (
    <div className="min-h-full bg-paper">
      <HeroHeader
        eyebrow={copy.eyebrow}
        title={copy.title}
        subtitle={copy.subtitle}
      />
      <div className="px-4 pt-4 pb-safe-or-6">
        <div className="relative">
          {children}
          <button
            type="button"
            onClick={() => void openExternalMap(openInMapsUrl(data.center.lat, data.center.lng))}
            className="absolute left-2 top-2 z-[500] inline-flex items-center gap-1.5 rounded-md bg-card px-3 py-2 text-body-sm font-medium text-ink shadow-elevated hairline"
          >
            <ExternalLink className="h-4 w-4 text-ink-secondary" strokeWidth={1.75} />
            Open in Maps
          </button>
        </div>

        <Section
          title={copy.locationsTitle}
          description={copy.locationsDescription}
        >
          <ListGroup>
            {sortedPins.map((pin) => (
              <ListItem
                key={pin.pin_id}
                title={pin.label}
                subtitle={pin.description || pinTypeLabel(pin)}
                leading={
                  <span className="flex h-10 w-10 items-center justify-center rounded-pill bg-primary-50 text-primary-700">
                    <MapPinIcon className="h-4.5 w-4.5" strokeWidth={1.75} />
                  </span>
                }
                trailing={<ExternalLink className="h-4 w-4 text-ink-muted" strokeWidth={1.75} />}
                onClick={() => void openExternalMap(openInMapsUrl(pin.lat, pin.lng))}
                noChevron
              />
            ))}
          </ListGroup>
        </Section>
      </div>
    </div>
  );
}

function WebMap({ data }: { data: MapData }): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      zoomControl: true,
      attributionControl: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);

    const markers: L.Marker[] = data.pins.map((pin) =>
      L.marker([pin.lat, pin.lng], { icon: locationMarkerIcon() })
        .bindPopup(buildPopupHtml(pin))
        .addTo(map),
    );

    if (data.pins.length === 1) {
      map.setView([data.pins[0]!.lat, data.pins[0]!.lng], 15);
    } else {
      const bounds = L.latLngBounds(data.pins.map((p) => [p.lat, p.lng] as [number, number]));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
    }

    mapRef.current = map;
    const resize = (): void => {
      map.invalidateSize();
    };
    window.addEventListener('resize', resize);
    setTimeout(resize, 120);

    return () => {
      window.removeEventListener('resize', resize);
      markers.forEach((m) => m.remove());
      map.remove();
      mapRef.current = null;
    };
  }, [data]);

  return (
    <MapChrome data={data} copy={pageCopy().map}>
      <div ref={containerRef} className="map-leaflet-host rounded-lg overflow-hidden hairline shadow-sm" />
    </MapChrome>
  );
}

function buildPopupHtml(pin: MapPin): string {
  const lines = [`<strong>${escapeHtml(pin.label)}</strong>`];
  if (pin.description) lines.push(escapeHtml(pin.description));
  return lines.join('<br/>');
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function NativeMap({ data }: { data: MapData }): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<GoogleMap | null>(null);

  useEffect(() => {
    let cancelled = false;
    let map: GoogleMap | null = null;

    (async () => {
      if (!containerRef.current) return;

      try {
        map = await GoogleMap.create({
          id: 'estate-map',
          element: containerRef.current,
          apiKey: (window as { __MAP_API_KEY__?: string }).__MAP_API_KEY__ ?? '',
          config: {
            center: data.center,
            zoom: data.zoom,
          },
        });
        if (cancelled) {
          await map.destroy();
          return;
        }
        mapRef.current = map;

        if (data.pins.length > 0) {
          await map.addMarkers(
            data.pins.map((pin) => ({
              coordinate: { lat: pin.lat, lng: pin.lng },
              title: pin.label,
              snippet: pin.description,
            })),
          );
        }
      } catch (e) {
        console.warn('[map] native init failed:', e);
      }
    })();

    return () => {
      cancelled = true;
      mapRef.current?.destroy().catch(() => { /* noop */ });
    };
  }, [data]);

  return (
    <MapChrome data={data} copy={pageCopy().map}>
      <div ref={containerRef} className="map-native-host rounded-lg overflow-hidden hairline shadow-sm" />
    </MapChrome>
  );
}

function pinTypeLabel(pin: MapPin): string {
  if (pin.type === 'security') return 'Security';
  if (pin.type === 'amenity') return 'Amenity';
  if (pin.type === 'property') return 'Property';
  return 'Location';
}

async function openExternalMap(url: string): Promise<void> {
  await Browser.open({ url }).catch(() => {
    window.open(url, '_blank', 'noopener,noreferrer');
  });
}
